// Small reusable form UI components
// Extracted from portal.jsx — Phase 3 Batch 1

import React from "react";
import { createPortal } from "react-dom";
import { STATUS_CONFIG } from "../../utils/formConstants";
import { money } from "../../utils/helpers";
import { sanitizeMoney } from "../../utils/sanitize";
import { Btn } from "../../utils/ui.jsx";

export function Badge({ status, style }) {
  const cfg = STATUS_CONFIG[status] || { colour: "#607D79", bg: "#CFE0DE" };
  return (
    <span
      style={Object.assign(
        {
          background: cfg.bg,
          color: cfg.colour,
          border: `1px solid ${cfg.colour}30`,
          borderRadius: 20,
          padding: "3px 12px",
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap",
        },
        style || {}
      )}
    >
      {status}
    </span>
  );
}

export function Sel({ label, value, onChange, options, required, placeholder = "Select..." }) {
  const selectId = React.useId();
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label
          htmlFor={selectId}
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: "#1A2E2B",
            marginBottom: 5,
          }}
        >
          {label}
          {required && <span style={{ color: "#C62828" }}> *</span>}
        </label>
      )}
      <select
        id={selectId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "9px 13px",
          borderRadius: 7,
          border: `1px solid ${"#CFE0DE"}`,
          fontSize: 14,
          color: value ? "#1A2E2B" : "#607D79",
          fontFamily: "inherit",
          background: "#FFFFFF",
          cursor: "pointer",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

export function QtyDrop({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{
        padding: "8px 10px",
        borderRadius: 7,
        border: `1px solid ${"#CFE0DE"}`,
        fontSize: 14,
        fontFamily: "inherit",
        background: "#FFFFFF",
        width: 70,
      }}
    >
      {[0, 1, 2, 3, 4].map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  );
}

export function CostFld({ value, onChange, max, label }) {
  const over = max && parseFloat(value) > max;
  return (
    <div style={{ flex: 1 }}>
      {label && <div style={{ fontSize: 12, color: "#607D79", marginBottom: 4 }}>{label}</div>}
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(sanitizeMoney(e.target.value))}
        onBlur={function (e) {
          if (max && parseFloat(e.target.value) > max) onChange(String(max));
        }}
        placeholder="0.00"
        style={{
          width: "100%",
          padding: "8px 10px",
          borderRadius: 7,
          border: `1px solid ${over ? "#C62828" : "#CFE0DE"}`,
          fontSize: 14,
          fontFamily: "inherit",
          boxSizing: "border-box",
        }}
      />
      {over && <div style={{ color: "#C62828", fontSize: 11, marginTop: 2 }}>Max ${max}</div>}
    </div>
  );
}

export function Total({ label, value }) {
  return (
    <div
      style={{
        background: "#E0F2F1",
        borderRadius: 8,
        padding: "10px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
      }}
    >
      <span style={{ fontWeight: 700, fontSize: 14, color: "#00695C" }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 18, color: "#00695C" }}>{money(value)}</span>
    </div>
  );
}

export function Warn({ children }) {
  return (
    <div
      style={{
        background: "#FFF8E1",
        border: "1px solid #FFB300",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 13,
        color: "#5D4037",
        marginBottom: 14,
      }}
    >
      [!] {children}
    </div>
  );
}

export function ChildCard({ index, onRemove, children }) {
  return (
    <div
      style={{
        border: `1px solid ${"#CFE0DE"}`,
        borderRadius: 10,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: "#F7F9F9",
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${"#CFE0DE"}`,
        }}
      >
        <span style={{ fontWeight: 700, color: "#00695C", fontSize: 15 }}>Child {index + 1}</span>
        <button
          onClick={onRemove}
          style={{
            background: "none",
            border: `1px solid ${"#C62828"}`,
            color: "#C62828",
            borderRadius: 6,
            padding: "4px 12px",
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Remove Child
        </button>
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

export function AddChild({ onClick, disabled, isEmpty }) {
  if (isEmpty)
    return (
      <div
        style={{
          textAlign: "center",
          padding: "32px 20px",
          border: `2px dashed ${"#CFE0DE"}`,
          borderRadius: 10,
          background: "#F7F9F9",
        }}
      >
        <div style={{ fontSize: 14, color: "#607D79", marginBottom: 14 }}>
          No children added to this section yet
        </div>
        <button
          onClick={onClick}
          style={{
            padding: "10px 28px",
            background: "#00897B",
            color: "white",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          + Add Child
        </button>
      </div>
    );
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? "#CFE0DE" : "#00897B",
        color: "#FFFFFF",
        border: "none",
        borderRadius: 8,
        padding: "9px 20px",
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        marginTop: 4,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      + Add Child{disabled ? " (max 4)" : ""}
    </button>
  );
}

export function FileField({ label, file, onChange, uploading }) {
  const id = `ff-${label.replace(/\s/g, "-")}`;
  const [dragOver, setDragOver] = React.useState(false);
  const isUploaded = typeof file === "string";
  const displayName = isUploaded
    ? file
        .split("/")
        .pop()
        .replace(/^[^_]+_/, "")
    : file
      ? file.name
      : null;
  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }
  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }
  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) onChange(files[0]);
  }
  // The drop zone is rendered as a real <button> so click + keyboard
  // (Enter/Space) both open the file picker. The hidden <input type="file">
  // is the actual control; the button triggers it programmatically.
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#1A2E2B",
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        id={id}
        style={{ display: "none" }}
        onChange={(e) => onChange(e.target.files[0])}
        disabled={uploading}
      />
      <button
        type="button"
        disabled={uploading}
        onClick={function () {
          const el = document.getElementById(id);
          if (el) el.click();
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          display: "block",
          width: "100%",
          fontFamily: "inherit",
          fontSize: 13,
          color: "#607D79",
          border: `2px dashed ${dragOver ? "#00897B" : "#CFE0DE"}`,
          borderRadius: 8,
          padding: "12px 16px",
          background: dragOver ? "#E0F2F1" : file ? "#E0F2F1" : "#F7F9F9",
          textAlign: "center",
          cursor: uploading ? "wait" : "pointer",
          transition: "border-color 0.15s, background 0.15s",
        }}
      >
        {uploading
          ? "Uploading..."
          : displayName
            ? `[ok] ${displayName}`
            : "[clip] Drop or click to choose (PDF/JPG/PNG, max 3MB)"}
      </button>
    </div>
  );
}

export function Grid2({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 4 }}>
      {children}
    </div>
  );
}

export function SubBox({ title, children }) {
  return (
    <div style={{ background: "#F7F9F9", borderRadius: 8, padding: 14, marginBottom: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#00695C", marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

export function LockedField({ label, value, wide }) {
  // No actual form control here — just a read-only display of a value with a
  // caption above it. Using <div> for the caption rather than <label> so we
  // don't claim a labelling relationship we don't have.
  return (
    <div style={{ marginBottom: 14, gridColumn: wide ? "span 2" : "span 1" }}>
      {label && (
        <div
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: "#607D79",
            marginBottom: 5,
          }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          padding: "9px 13px",
          borderRadius: 7,
          background: "#F5F7F7",
          border: "1px solid #CFE0DE",
          fontSize: 14,
          color: "#1A2E2B",
          minHeight: 38,
        }}
      >
        {value || <span style={{ color: "#aaa", fontStyle: "italic" }}>-</span>}
      </div>
    </div>
  );
}

/**
 * Reusable modal backdrop + container.
 *
 * Renders a transparent <button> covering the viewport as the dimmed
 * backdrop, plus the modal content as a sibling positioned over the top.
 * Using a real <button> for the backdrop gives mouse, keyboard, and screen
 * reader users the same way to dismiss the modal (click, Enter on focused
 * backdrop, or the document-level Escape listener installed below).
 *
 * Usage:
 *   {open && (
 *     <ModalBackdrop onClose={() => setOpen(false)}>
 *       <div style={{ background: "white", borderRadius: 10, padding: 24, ... }}>
 *         ...modal content...
 *       </div>
 *     </ModalBackdrop>
 *   )}
 */
// Open-modal stack, in mount order. Escape closes ONLY the topmost modal — without
// this, every mounted ModalBackdrop's window keydown listener fires and a single
// Escape collapses the whole stack (e.g. a confirm over an edit modal over a call
// modal). Backward-compatible: a lone modal is always topmost, so Escape still closes
// it. (Z-index stacking already works — DialogHost uses a high zIndex and portal
// modals paint last; this fixes the one remaining nested-modal defect.)
const openModalStack = [];

export function ModalBackdrop({
  onClose,
  children,
  contentStyle,
  portal,
  zIndex = 9999,
  draggable = false,
  dragTitle = "",
  // Header-handle mode: pass a ref that the caller also attaches to their OWN
  // header element, and that header becomes the drag handle (no built-in title
  // bar is rendered). Lets a modal that already has a header be draggable without
  // stacking a second bar. When omitted, `draggable` + `dragTitle` render the
  // built-in teal bar as before.
  dragHandleRef,
}) {
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  // Latest offset in a ref so the pointermove handler (attached once per drag)
  // always reads the current base without a stale closure.
  const offsetRef = React.useRef(offset);
  offsetRef.current = offset;

  // Unique id for this modal instance, used to tell whether we're the TOPMOST open
  // modal (see openModalStack above). Register on mount / unregister on unmount, so
  // the stack reflects the order modals were opened.
  const modalId = React.useId();
  React.useEffect(
    function () {
      openModalStack.push(modalId);
      return function () {
        const i = openModalStack.indexOf(modalId);
        if (i !== -1) openModalStack.splice(i, 1);
      };
    },
    [modalId]
  );

  React.useEffect(
    function () {
      function onKey(e) {
        if (e.key !== "Escape") return;
        // Only the topmost open modal responds to Escape, so one keypress can't
        // collapse a whole stack of nested modals.
        if (openModalStack[openModalStack.length - 1] !== modalId) return;
        onClose();
      }
      window.addEventListener("keydown", onKey);
      return function () {
        window.removeEventListener("keydown", onKey);
      };
    },
    [onClose, modalId]
  );

  // Re-centre whenever the modal's subject changes (i.e. opening a different item).
  React.useEffect(
    function () {
      setOffset({ x: 0, y: 0 });
    },
    [dragTitle]
  );

  // Drag by the title bar (draggable mode only). The offset is layered on top of
  // the translate(-50%,-50%) centring, so the modal still opens centred.
  const onHandleDown = React.useCallback(function (e) {
    if (e.button != null && e.button !== 0) return;
    // Don't start a drag when the pointer went down on an interactive control in
    // the handle (buttons/inputs/links) — let that click through untouched.
    if (e.target?.closest?.("button, a, input, textarea, select, label")) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const base = offsetRef.current;
    function onMove(ev) {
      setOffset({ x: base.x + (ev.clientX - startX), y: base.y + (ev.clientY - startY) });
    }
    function onUp() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  // Header-handle mode: bind the caller's own header element as the drag handle.
  React.useEffect(
    function () {
      if (!draggable) return;
      const el = dragHandleRef?.current;
      if (!el) return;
      el.style.cursor = "move";
      el.style.userSelect = "none";
      el.addEventListener("pointerdown", onHandleDown);
      return function () {
        el.removeEventListener("pointerdown", onHandleDown);
      };
    },
    [draggable, dragHandleRef, onHandleDown]
  );

  const dialogTransform = draggable
    ? `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`
    : "translate(-50%, -50%)";

  const content = (
    <>
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          border: "none",
          padding: 0,
          margin: 0,
          cursor: "default",
          zIndex: zIndex,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={Object.assign(
          {
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: dialogTransform,
            zIndex: zIndex + 1,
            maxWidth: "calc(100vw - 40px)",
            maxHeight: "calc(100vh - 40px)",
            overflow: "auto",
            // Dark default text colour. By default ModalBackdrop renders inline
            // (not via a portal), so without this the modal inherits the
            // surrounding panel's colour — e.g. white inside the dark Approval
            // Decisions header — making inputs/text invisible. Callers can
            // override via contentStyle, and child elements that set their own
            // colour win.
            color: "#1A2E2B",
          },
          contentStyle || {}
        )}
      >
        {draggable && dragTitle && (
          <div
            onPointerDown={onHandleDown}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "move",
              userSelect: "none",
              padding: "9px 14px",
              background: "#00695C",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <span style={{ flex: 1 }}>{dragTitle || "Drag to move"}</span>
            <button
              type="button"
              aria-label="Close dialog"
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
                padding: "0 2px",
              }}
            >
              ×
            </button>
          </div>
        )}
        {children}
      </div>
    </>
  );
  // portal: render at document.body so the modal escapes any surrounding
  // stacking context (otherwise positioned form elements can paint over it —
  // e.g. the wizard's school field / warnings bleeding through the Help modal).
  return portal && typeof document !== "undefined" ? createPortal(content, document.body) : content;
}

export function LockedBanner() {
  return (
    <div
      style={{
        background: "#FFEBEE",
        border: "1px solid #EF9A9A",
        borderRadius: 8,
        padding: "10px 16px",
        marginBottom: 16,
        fontSize: 13,
        color: "#C62828",
        fontWeight: 600,
      }}
    >
      This request has been submitted and cannot be edited. &nbsp;You can still add comments and
      upload files below.
    </div>
  );
}

export function WizardStepBar({ step, step2Label = "Children & needs" }) {
  const steps = [
    { n: 1, label: "General info" },
    { n: 2, label: step2Label },
    { n: 3, label: "Review & submit" },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 28 }}>
      {steps.map(function (s, i) {
        const active = step === s.n;
        const done = step > s.n;
        const bg = done ? "#00897B" : active ? "#00695C" : "transparent";
        const col = done || active ? "white" : "#607D79";
        const bdr = done ? "#00897B" : active ? "#00695C" : "#CFE0DE";
        return (
          <React.Fragment key={s.n}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: bg,
                  border: `2px solid ${bdr}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  color: col,
                  flexShrink: 0,
                }}
              >
                {done ? "\u2713" : s.n}
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: active ? 700 : 400,
                  color: active ? "#00695C" : done ? "#00897B" : "#607D79",
                  whiteSpace: "nowrap",
                }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: step > s.n ? "#00897B" : "#CFE0DE",
                  margin: "0 10px",
                  minWidth: 20,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function SubmittedScreen({ refNo, onBack }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F5F7F7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>[ok]</div>
        <h2 style={{ color: "#00695C", margin: "0 0 12px" }}>Request Submitted!</h2>
        <p style={{ color: "#607D79", marginBottom: 8 }}>Your reference number is:</p>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "#00897B",
            background: "#E0F2F1",
            borderRadius: 10,
            padding: "12px 24px",
            display: "inline-block",
            marginBottom: 24,
          }}
        >
          {refNo}
        </div>
        <p style={{ color: "#607D79", fontSize: 14, marginBottom: 24 }}>
          A member of the Zephyr team will review your request, typically within 24 hours. This
          reference number confirms submission {"\u2014"} it is not an approval.
        </p>
        <Btn onClick={onBack} style={{ fontSize: 15, padding: "12px 28px" }}>
          Return to My Requests
        </Btn>
      </div>
    </div>
  );
}

// A small accessible on/off toggle switch (role="switch"). Used for boolean fields
// that read better as a slider than a Yes/No dropdown (Taxable, Stop Credit). The
// ON colour is configurable so a "stop"-style flag can be red while a positive flag
// is teal. Controlled: pass `checked` + `onChange(next)`. Respects `disabled`
// (used inside a read-only <fieldset disabled>).
export function Toggle({ id, checked, onChange, disabled = false, label, onColor = "#00695C" }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={function () {
        if (!disabled) onChange(!checked);
      }}
      style={{
        width: 46,
        height: 26,
        borderRadius: 13,
        border: "none",
        padding: 0,
        flexShrink: 0,
        background: checked ? onColor : "#B0BEC5",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative",
        transition: "background 0.15s",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 23 : 3,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          transition: "left 0.15s",
        }}
      />
    </button>
  );
}
